import { cloneObject } from '../../common-functions';
import type { TypeOrArray } from '../../common-interfaces';
import type { DropDownResult } from './drop-down-types';

export interface DropDownState {
  selectedValue?: Array<DropDownResult>;
  showPlaceholder: boolean;
  showClearButton: boolean;
  isListOpen: boolean;
  isPassedValue: boolean;
}

export const DropDownActionType = {
  SetSelectedValue: 0,
  UpdateSelectedValue: 1,
  UpdateMultiSelectedValue: 2,
  UpdateSelectionIndicators: 3,
  ClearSelectedValue: 4,
  SetPassedValue: 5,
} as const;
export type DropDownActionType = (typeof DropDownActionType)[keyof typeof DropDownActionType];

export interface DropDownReducerAction {
  type: DropDownActionType;
  selectedValue?: Array<DropDownResult>;
  passedValue?: TypeOrArray<DropDownResult>;
  showPlaceholder?: boolean;
  showClearButton?: boolean;
  isListOpen?: boolean;
}

export default function reducer(state: DropDownState, action: DropDownReducerAction): DropDownState {
  switch (action.type) {
    case DropDownActionType.SetSelectedValue:
      if (action.selectedValue) {
        return cloneObject(state, {
          selectedValue: action.selectedValue,
          isPassedValue: false,
        });
      }

      return cloneObject(state, {
        isPassedValue: false,
      });

    case DropDownActionType.UpdateSelectedValue:
      if (action.selectedValue) {
        let currentSelectedValue: Array<DropDownResult>;
        const actionSelectedValue = action.selectedValue;

        if (
          state.selectedValue?.find(
            (value) => actionSelectedValue.findIndex((actionValue) => actionValue.value === value.value) > -1
          )
        ) {
          // if the action selectedValue already exists in the states selectedValue then this item needs to be removed
          // from the selection
          currentSelectedValue = state.selectedValue?.filter(
            (value) => actionSelectedValue.findIndex((actionValue) => actionValue.value === value.value) === -1
          );
        } else {
          // otherwise, just add the item onto the selected value list
          currentSelectedValue = [...(state.selectedValue || []), ...action.selectedValue];
        }

        return cloneObject(state, {
          selectedValue: currentSelectedValue,
          isPassedValue: false,
        });
      }

      return cloneObject(state, {
        isPassedValue: false,
      });
    case DropDownActionType.UpdateMultiSelectedValue:
      if (action.selectedValue) {
        const filteredValues = state.selectedValue
          ? action.selectedValue.filter(
              (item) => !state.selectedValue?.some((selectedItem) => selectedItem.value === item.value)
            )
          : action.selectedValue;

        return cloneObject(state, {
          selectedValue: [...(state.selectedValue || []), ...filteredValues],
          isPassedValue: false,
        });
      }

      return cloneObject(state, {
        isPassedValue: false,
      });

    case DropDownActionType.UpdateSelectionIndicators:
      return cloneObject(state, {
        showPlaceholder: action.showPlaceholder ?? state.showPlaceholder,
        showClearButton: action.showClearButton ?? state.showClearButton,
        isPassedValue: false,
      });

    case DropDownActionType.ClearSelectedValue:
      return cloneObject(state, {
        selectedValue: undefined,
        isPassedValue: false,
      });

    case DropDownActionType.SetPassedValue:
      if (action.passedValue) {
        return cloneObject(state, {
          selectedValue: Array.isArray(action.passedValue) ? action.passedValue : [action.passedValue],
          isPassedValue: true,
        });
      }

      return cloneObject(state, {
        isPassedValue: false,
      });

    default:
      return cloneObject(state, {
        isPassedValue: false,
      });
  }
}
